/**
 * Slide-out drawer showing conversation history.
 *
 * Opens from the left with a semi-transparent backdrop.
 * Contains: "New Chat" button + scrollable conversation list.
 */

import { useEffect, useCallback } from "react";
import { X, SquarePen, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { ConversationItem } from "./ConversationItem";
import { useConversations } from "../hooks/useConversations";
import { useConversationActions } from "../hooks/useConversationActions";
import type { ConversationSummary } from "../types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConversationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConversationDrawer({
  isOpen,
  onClose,
  activeConversationId,
  onSelectConversation,
  onNewChat,
}: ConversationDrawerProps) {
  const {
    conversations,
    loading,
    error,
    hasMore,
    refetch,
    loadMore,
    removeFromList,
    updateTitleInList,
  } = useConversations();

  const { renameConversation, deleteConversation } = useConversationActions();

  // Fetch conversations when drawer opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // ── Handlers ────────────────────────────────────────────────────────

  const handleSelect = useCallback(
    (id: string) => {
      onSelectConversation(id);
      onClose();
    },
    [onSelectConversation, onClose],
  );

  const handleNewChat = useCallback(() => {
    onNewChat();
    onClose();
  }, [onNewChat, onClose]);

  const handleDelete = useCallback(
    async (id: string) => {
      // Optimistic removal
      removeFromList(id);
      const success = await deleteConversation(id);
      if (!success) {
        // Refetch to restore if deletion failed
        refetch();
      }
    },
    [removeFromList, deleteConversation, refetch],
  );

  const handleRename = useCallback(
    async (id: string, title: string) => {
      // Optimistic update
      updateTitleInList(id, title);
      const result = await renameConversation(id, title);
      if (!result) {
        // Refetch to restore if rename failed
        refetch();
      }
    },
    [updateTitleInList, renameConversation, refetch],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 100;
      if (nearBottom && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore],
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className="fixed inset-y-0 left-0 z-[61] flex w-[300px] max-w-[85vw] flex-col bg-surface shadow-lg"
        role="dialog"
        aria-label="Conversation history"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-text-primary">
            Conversations
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-touch w-touch items-center justify-center rounded-full text-text-muted active:bg-brand/5"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* New Chat button */}
        <div className="border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={handleNewChat}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5",
              "bg-brand text-text-inverse font-medium text-sm",
              "active:bg-brand/90 transition-colors",
            )}
          >
            <SquarePen className="h-4 w-4" />
            New Chat
          </button>
        </div>

        {/* Conversation list */}
        <div
          className="flex-1 overflow-y-auto"
          onScroll={handleScroll}
        >
          {error && (
            <div className="px-4 py-3 text-xs text-error">{error}</div>
          )}

          {conversations.length === 0 && !loading && !error && (
            <div className="flex flex-col items-center justify-center px-4 py-8 text-text-muted">
              <p className="text-sm">No conversations yet</p>
              <p className="mt-1 text-xs">Start chatting to see your history here</p>
            </div>
          )}

          {conversations.map((conv: ConversationSummary) => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isActive={conv.id === activeConversationId}
              onSelect={handleSelect}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}

          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-brand" />
            </div>
          )}

          {hasMore && !loading && conversations.length > 0 && (
            <button
              type="button"
              onClick={loadMore}
              className="w-full py-3 text-center text-xs text-brand active:bg-brand/5"
            >
              Load more
            </button>
          )}
        </div>
      </div>
    </>
  );
}
